import { ToWords } from "to-words";
import DownloadButton from "../DownloadButton";
import { convertAmountToWords } from "../../utils/Formatter";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/POInvoiceLayout.css";

export const PIInvoiceLayout = ({
  headDetails,
  companyData,
  itemDetails,
  rowData,
  invoiceTableData,
}) => {
  const toWords = new ToWords();
  const organizationData = JSON.parse(localStorage.getItem("organisation"));

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "0.00";
    return Number(amount).toFixed(2);
  };

  const formatCompanyAddress = (companyData) => {
    if (!companyData) return "N/A";

    const parts = [
      companyData.address?.trim(),
      companyData.city?.trim(),
    ].filter(Boolean);

    const pin = companyData.pincode ? `- ${companyData.pincode}` : "";

    return parts.length > 0 || pin
      ? `${parts.join(", ")} ${pin}`.trim()
      : "N/A";
  };

  const calculateTaxFromItems = () => {
    if (!itemDetails || !Array.isArray(itemDetails)) {
      return {
        cgst: 0,
        sgst: 0,
        igst: 0,
        totalGst: 0,
        cgstPercent: 0,
        sgstPercent: 0,
        igstPercent: 0,
      };
    }

    const taxTotals = itemDetails.reduce(
      (totals, item) => {
        const cgstAmt = parseFloat(item.cgst_amt || 0);
        const sgstAmt = parseFloat(item.sgst_amt || 0);
        const igstAmt = parseFloat(item.igst_amt || 0);
        const cgstPer = parseFloat(item.cgst_per || 0);
        const sgstPer = parseFloat(item.sgst_per || 0);
        const igstPer = parseFloat(item.igst_per || 0);

        return {
          cgst: totals.cgst + cgstAmt,
          sgst: totals.sgst + sgstAmt,
          igst: totals.igst + igstAmt,
          cgstPer: totals.cgstPer + cgstPer,
          sgstPer: totals.sgstPer + sgstPer,
          igstPer: totals.igstPer + igstPer,
        };
      },
      { cgst: 0, sgst: 0, igst: 0, cgstPer: 0, sgstPer: 0, igstPer: 0 },
    );

    const totalGst = taxTotals.cgst + taxTotals.sgst + taxTotals.igst;

    const itemCount = itemDetails.length;
    const cgstPercent =
      itemCount > 0 ? (taxTotals.cgstPer / itemCount).toFixed(2) : 0;
    const sgstPercent =
      itemCount > 0 ? (taxTotals.sgstPer / itemCount).toFixed(2) : 0;
    const igstPercent =
      itemCount > 0 ? (taxTotals.igstPer / itemCount).toFixed(2) : 0;

    return {
      cgst: taxTotals.cgst.toFixed(2),
      sgst: taxTotals.sgst.toFixed(2),
      igst: taxTotals.igst.toFixed(2),
      totalGst: totalGst.toFixed(2),
      cgstPercent,
      sgstPercent,
      igstPercent,
    };
  };

  const calculateTotalFromItems = () => {
    if (!itemDetails || !Array.isArray(itemDetails)) {
      return 0;
    }

    const totalAmount = itemDetails.reduce((sum, item) => {
      const amount = parseFloat(item.amount || 0);
      return sum + amount;
    }, 0);

    return totalAmount;
  };

  const { cgst, sgst, igst, totalGst, cgstPercent, sgstPercent, igstPercent } =
    calculateTaxFromItems();
  const totalAmountFromItems = calculateTotalFromItems();

  const getItemValue = (item, names, fallback = "-") => {
    const name = names.find(
      (key) =>
        item?.[key] !== undefined && item?.[key] !== null && item[key] !== "",
    );
    return name ? item[name] : fallback;
  };

  const proformaInvoiceNumber = getItemValue(
    headDetails,
    ["tran_no", "proforma_invoice_no", "invoice_no"],
    "-",
  );
  const salesOrderNumber = getItemValue(
    headDetails,
    ["sale_order_no", "sales_order_no", "sales_order_number", "ref_tran_no"],
    "-",
  );
  const salesOrderDate = getItemValue(
    headDetails,
    ["sale_order_date", "sales_order_date", "ref_tran_date"],
    "",
  );
  const customerPoNumber = getItemValue(
    headDetails,
    [
      "po_no",
      "customer_po_no",
      "customer_po_number",
      "buyer_order_no",
      "po_number",
    ],
    "-",
  );
  const customerPoDate = getItemValue(
    headDetails,
    ["po_date", "customer_po_date", "buyer_order_date"],
    "",
  );
  const quotationNumber = getItemValue(
    headDetails,
    ["quotation_no", "quotation_number"],
    "-",
  );
  const quotationDate = getItemValue(headDetails, ["quotation_date"], "");
  const deliveryDate = getItemValue(headDetails, ["delivery_date"], "");

  const freightCharges = Number(
    getItemValue(headDetails, ["freight_charges", "freight", "freight_amt"], 0),
  );
  const packingCharges = Number(
    getItemValue(
      headDetails,
      ["packing_charges", "packing_charge", "packing_amt"],
      0,
    ),
  );
  const discount = Number(
    getItemValue(headDetails, ["discount", "discount_amt"], 0),
  );
  const subTotal = totalAmountFromItems + freightCharges + packingCharges;
  const subTotalAfterDiscount = subTotal - discount;
  const finalTotal = subTotalAfterDiscount + Number(totalGst);
  const amountInWords = convertAmountToWords(finalTotal, toWords);

  const customerAddress =
    companyData.company_address || formatCompanyAddress(companyData) || "N/A";
  const descriptionField = Array.isArray(rowData)
    ? rowData.find((field) =>
        /description|product|goods|item/i.test(
          `${field.title || ""} ${field.field_name || ""}`,
        ),
      )
    : null;
  const descriptionFields = [
    "product_name",
    "pur_product_name",
    "product_description",
    "description",
    "description_of_goods",
    "item_description",
    "item_name",
    "product_details",
    "name",
    descriptionField?.field_name,
  ].filter(Boolean);

  return (
    <div className="invoice-container proforma-invoice-layout invoice-page-container">
      <div className="invoice-box">
        <div className="invoice-left">
          <div className="invoice-logo-container">
            <img
              src={organizationData.logo_url || ProfilePlaceholder}
              alt="Logo"
              className="invoice-logo-content"
            />
          </div>
          <div>
            <h3 className="invoice-company-name">
              {organizationData.name || "N/A"}
            </h3>
            <p className="invoice-company-address">
              {formatCompanyAddress(organizationData)}
              <br />
              GSTIN: {organizationData.gst_no || "N/A"}
              <br />
              State: {organizationData.state || "N/A"}
              <br />
              Email: enquiry@finepunch.in
              <br />
              Website: www.finepunch.in
            </p>
          </div>
        </div>

        <h2 className="invoice-title proforma-center-title">
          Proforma Invoice
        </h2>

        <div className="invoice-right proforma-reference-details">
          {[
            { label: "Proforma Invoice No", value: proformaInvoiceNumber },
            { label: "Customer PO No", value: customerPoNumber },
            {
              label: "Customer PO Date",
              value: customerPoDate ? formatDate(customerPoDate) : "-",
            },
            { label: "Sales Order No", value: salesOrderNumber },
            {
              label: "Sales Order Date",
              value: salesOrderDate ? formatDate(salesOrderDate) : "-",
            },
            { label: "Quotation No", value: quotationNumber },
            {
              label: "Quotation Date",
              value: quotationDate ? formatDate(quotationDate) : "-",
            },
            {
              label: "Delivery Date",
              value: deliveryDate ? formatDate(deliveryDate) : "-",
            },
          ].map((item) => (
            <div className="purchase-detail" key={item.label}>
              <p className="detail-label">
                <strong>{item.label}:</strong>
              </p>
              <div className="detail-value">{item.value || "N/A"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="invoice-supplier-details-container proforma-parties">
        <div className="invoice-supplier-section">
          <h4 className="invoice-section-title">Name of Customer</h4>
          <p className="invoice-section-text">
            <strong>{companyData.company_name || "N/A"}</strong>
            <br />
            Address: {customerAddress}
            <br />
            State: {companyData.state || companyData.company_state || "N/A"}
            <br />
            GSTIN/UIN:{" "}
            {companyData.gst_no || companyData.company_gst_no || "N/A"}
          </p>
        </div>
        <div className="invoice-delivery-section">
          <h4 className="invoice-section-title">Delivery at</h4>
          <p className="invoice-section-text">
            <strong>{companyData.company_name || "N/A"}</strong>
            <br />
            Address: {companyData.delivery_address || customerAddress}
            <br />
            State: {companyData.state || companyData.company_state || "N/A"}
            <br />
            GSTIN/UIN:{" "}
            {companyData.gst_no || companyData.company_gst_no || "N/A"}
          </p>
        </div>
        <div className="invoice-delivery-section proforma-terms-section">
          <h4 className="invoice-section-title">Terms and Conditions</h4>
          <p className="invoice-section-text">
            {headDetails.payment_terms ||
              organizationData.payment_terms ||
              "N/A"}
          </p>
        </div>
      </div>

      <table className="invoice-table proforma-items">
        <thead>
          <tr>
            <th className="invoice-th">No.</th>
            <th className="invoice-th">Product Name</th>
            <th className="invoice-th">Description of Goods</th>
            <th className="invoice-th">Quantity</th>
            <th className="invoice-th">UOM</th>
            <th className="invoice-th">Rate</th>
            <th className="invoice-th">Amount</th>
            <th className="invoice-th">Gross Amount</th>
          </tr>
        </thead>
        <tbody>
          {itemDetails.map((item, index) => (
            <tr key={index}>
              <td className="invoice-td">{index + 1}</td>
              <td className="invoice-td">
                {getItemValue(item, ["product_name", "name"])}
              </td>
              <td className="invoice-td">
                {getItemValue(item, ["product_desc", ...descriptionFields])}
              </td>
              <td className="invoice-td">
                {getItemValue(item, ["quantity", "pur_quantity", "qty"])}
              </td>
              <td className="invoice-td">
                {getItemValue(item, ["unit", "uom"])}
              </td>
              <td className="invoice-td">
                {getItemValue(item, ["rate", "unit_rate"])}
              </td>
              <td className="invoice-td">
                {getItemValue(item, ["amount", "taxable_value"])}
              </td>
              <td className="invoice-td">
                {getItemValue(item, ["gross_amt", "total_amount", "amount"])}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="invoice-tax-summary proforma-tax-summary">
        <div className="invoice-tax-summary-row">
          <div className="invoice-tax-summary-cell">
            <p className="invoice-tax-summary-label">CGST @ {cgstPercent}%</p>
            <p className="invoice-tax-summary-value">{formatCurrency(cgst)}</p>
          </div>
          <div className="invoice-tax-summary-cell">
            <p className="invoice-tax-summary-label">SGST @ {sgstPercent}%</p>
            <p className="invoice-tax-summary-value">{formatCurrency(sgst)}</p>
          </div>
          <div className="invoice-tax-summary-cell">
            <p className="invoice-tax-summary-label">IGST @ {igstPercent}%</p>
            <p className="invoice-tax-summary-value">{formatCurrency(igst)}</p>
          </div>
          <div className="invoice-tax-summary-cell">
            <p className="invoice-tax-summary-label">Net Amount (Before GST)</p>
            <p className="invoice-tax-summary-value">
              {formatCurrency(totalAmountFromItems)}
            </p>
          </div>
          <div className="invoice-tax-summary-cell">
            <p className="invoice-tax-summary-label">Total GST Amount</p>
            <p className="invoice-tax-summary-value">
              {formatCurrency(totalGst)}
            </p>
          </div>
        </div>
      </div>

      <div className="invoice-grid-container proforma-lower">
        <div className="invoice-left-pane proforma-total-words">
          <strong>Total Amount:</strong>
          <span>{amountInWords} Only</span>
        </div>
        <div className="invoice-right-pane proforma-summary">
          <p>
            <strong>Total:</strong>
            <span>{formatCurrency(finalTotal)}</span>
          </p>
        </div>
      </div>

      <div className="proforma-bank-footer">
        <div className="proforma-footer-information">
          <section className="proforma-footer-declaration">
            <strong>Declaration:</strong>
            <p>
              We declare that this invoice shows the actual price of the goods
              described and that all particulars are true and correct.
            </p>
          </section>
          <section className="proforma-footer-bank">
            <strong>Company Bank Details:</strong>
            <p>Bank Name: Bank Of Baroda - Jigani</p>
            <p>Account Name: FINE PUNCH FAB PRIVATE LIMITED</p>
            <p>ACCOUNT NO: 67610400000783</p>
            <p>Branch & IFSC: Jigani Branch-560105 & BARB0VJJIGA</p>
          </section>
        </div>
        <div className="proforma-authorized">
          <div className="proforma-signature-name">
            <strong>
              For {organizationData.name || "Fine Punch Fab Pvt. Ltd."}
            </strong>
            <span>PRASANNA CHANDRA SHETTY</span>
          </div>
          <span>Authorised Signatory</span>
        </div>
      </div>

      <div className="proforma-print-footer">
        <div className="proforma-prepared-by">
          <strong>Prepared By:</strong>
        </div>
        <p className="proforma-system-note">
          This is a system-generated document and does not require a signature.
        </p>
      </div>

      <div className="invoice-buttons proforma-download">
        <DownloadButton
          tableData={invoiceTableData}
          fileName="proforma-invoice"
          downloadFormats={["excel", "pdf", "full-page-pdf"]}
          fullPageDownload={true}
          fullPageSelector=".invoice-page-container"
        />
      </div>
    </div>
  );
};
